// ไฟล์: frontend/src/pages/admin/AdminResourcesPage.js
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import "./AdminStyles.css";
import AdminSidebar from "../../components/layout/AdminSidebar";

const AdminResourcesPage = ({ user }) => {
  const [resources, setResources] = useState([]);
  const [pendingResources, setPendingResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("approved");

  // แยก ref สำหรับแต่ละตาราง
  const approvedTableRef = useRef(null);
  const pendingTableRef = useRef(null);
  const approvedDataTableRef = useRef(null);
  const pendingDataTableRef = useRef(null);

  // ดึงข้อมูลสื่อการสอนทั้งหมด
  useEffect(() => {
    const fetchResources = async () => {
      try {
        // ดึงสื่อการสอนที่อนุมัติแล้ว
        const approvedResponse = await axios.get("/resources");
        setResources(approvedResponse.data.resources);

        // ดึงสื่อการสอนที่รอการอนุมัติ
        const pendingResponse = await axios.get("/resources/pending/all");
        setPendingResources(pendingResponse.data.resources);

        setLoading(false);
      } catch (error) {
        console.error("Error fetching resources:", error);
        setLoading(false);
      }
    };

    fetchResources();
  }, []);

  // เริ่มต้น DataTable สำหรับตารางที่อนุมัติแล้ว
  useEffect(() => {
    if (!loading && activeTab === "approved" && approvedTableRef.current) {
      // ทำลาย DataTable เก่าก่อนถ้ามี
      if (approvedDataTableRef.current) {
        approvedDataTableRef.current.destroy();
        approvedDataTableRef.current = null;
      }

      // รอให้ DOM อัปเดตก่อนสร้าง DataTable
      setTimeout(() => {
        if (window.$ && window.$.fn.DataTable && approvedTableRef.current) {
          approvedDataTableRef.current = window
            .$(approvedTableRef.current)
            .DataTable({
              language: {
                processing: "กำลังประมวลผล...",
                search: "ค้นหา:",
                lengthMenu: "แสดง _MENU_ รายการ",
                info: "แสดง _START_ ถึง _END_ จาก _TOTAL_ รายการ",
                infoEmpty: "แสดง 0 ถึง 0 จาก 0 รายการ",
                infoFiltered: "(กรองจากทั้งหมด _MAX_ รายการ)",
                loadingRecords: "กำลังโหลดข้อมูล...",
                zeroRecords: "ไม่พบข้อมูล",
                emptyTable: "ไม่มีข้อมูลในตาราง",
                paginate: {
                  first: "หน้าแรก",
                  previous: "ก่อนหน้า",
                  next: "ถัดไป",
                  last: "หน้าสุดท้าย",
                },
                aria: {
                  sortAscending: ": เรียงจากน้อยไปมาก",
                  sortDescending: ": เรียงจากมากไปน้อย",
                },
              },
              responsive: true,
              dom: "Bfrtip",
              buttons: ["copy", "excel", "pdf", "print"],
            });
        }
      }, 100);
    }

    // Cleanup function
    return () => {
      if (approvedDataTableRef.current) {
        approvedDataTableRef.current.destroy();
        approvedDataTableRef.current = null;
      }
    };
  }, [loading, activeTab, resources]);

  // เริ่มต้น DataTable สำหรับตารางที่รอการอนุมัติ
  useEffect(() => {
    if (
      !loading &&
      activeTab === "pending" &&
      pendingTableRef.current &&
      pendingResources.length > 0
    ) {
      // ทำลาย DataTable เก่าก่อนถ้ามี
      if (pendingDataTableRef.current) {
        pendingDataTableRef.current.destroy();
        pendingDataTableRef.current = null;
      }

      // รอให้ DOM อัปเดตก่อนสร้าง DataTable
      setTimeout(() => {
        if (window.$ && window.$.fn.DataTable && pendingTableRef.current) {
          pendingDataTableRef.current = window
            .$(pendingTableRef.current)
            .DataTable({
              language: {
                processing: "กำลังประมวลผล...",
                search: "ค้นหา:",
                lengthMenu: "แสดง _MENU_ รายการ",
                info: "แสดง _START_ ถึง _END_ จาก _TOTAL_ รายการ",
                infoEmpty: "แสดง 0 ถึง 0 จาก 0 รายการ",
                infoFiltered: "(กรองจากทั้งหมด _MAX_ รายการ)",
                loadingRecords: "กำลังโหลดข้อมูล...",
                zeroRecords: "ไม่พบข้อมูล",
                emptyTable: "ไม่มีข้อมูลในตาราง",
                paginate: {
                  first: "หน้าแรก",
                  previous: "ก่อนหน้า",
                  next: "ถัดไป",
                  last: "หน้าสุดท้าย",
                },
                aria: {
                  sortAscending: ": เรียงจากน้อยไปมาก",
                  sortDescending: ": เรียงจากมากไปน้อย",
                },
              },
              responsive: true,
              dom: "Bfrtip",
              buttons: ["copy", "excel", "pdf", "print"],
            });
        }
      }, 100);
    }

    // Cleanup function
    return () => {
      if (pendingDataTableRef.current) {
        pendingDataTableRef.current.destroy();
        pendingDataTableRef.current = null;
      }
    };
  }, [loading, activeTab, pendingResources]);

  // อนุมัติสื่อการสอน
  const handleApprove = async (id) => {
    try {
      await axios.put(`/resources/${id}/approve`);

      Swal.fire({
        icon: "success",
        title: "อนุมัติสำเร็จ",
        text: "สื่อการสอนได้รับการอนุมัติเรียบร้อยแล้ว",
        confirmButtonText: "ตกลง",
      });

      // อัพเดทรายการสื่อการสอน
      const updatedPending = pendingResources.filter(
        (resource) => resource.id !== id
      );
      setPendingResources(updatedPending);

      // ดึงข้อมูลสื่อการสอนที่อนุมัติแล้วใหม่
      const approvedResponse = await axios.get("/resources");
      setResources(approvedResponse.data.resources);
    } catch (error) {
      console.error("Error approving resource:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: "ไม่สามารถอนุมัติสื่อการสอนได้",
        confirmButtonText: "ตกลง",
      });
    }
  };

  // ลบสื่อการสอน
  const handleDelete = async (id) => {
    try {
      const result = await Swal.fire({
        icon: "warning",
        title: "ยืนยันการลบ",
        text: "คุณต้องการลบสื่อการสอนนี้ใช่หรือไม่?",
        showCancelButton: true,
        confirmButtonText: "ลบ",
        cancelButtonText: "ยกเลิก",
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
      });

      if (result.isConfirmed) {
        await axios.delete(`/resources/${id}`);

        Swal.fire({
          icon: "success",
          title: "ลบสำเร็จ",
          text: "ลบสื่อการสอนเรียบร้อยแล้ว",
          confirmButtonText: "ตกลง",
        });

        // อัพเดทรายการสื่อการสอน
        if (activeTab === "approved") {
          const updatedResources = resources.filter(
            (resource) => resource.id !== id
          );
          setResources(updatedResources);
        } else {
          const updatedPending = pendingResources.filter(
            (resource) => resource.id !== id
          );
          setPendingResources(updatedPending);
        }
      }
    } catch (error) {
      console.error("Error deleting resource:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: "ไม่สามารถลบสื่อการสอนได้",
        confirmButtonText: "ตกลง",
      });
    }
  };

  // สลับ Tab
  const handleTabChange = (tab) => {
    // ทำลาย DataTable ที่ใช้งานอยู่ก่อนเปลี่ยน tab
    if (activeTab === "approved" && approvedDataTableRef.current) {
      approvedDataTableRef.current.destroy();
      approvedDataTableRef.current = null;
    } else if (activeTab === "pending" && pendingDataTableRef.current) {
      pendingDataTableRef.current.destroy();
      pendingDataTableRef.current = null;
    }

    setActiveTab(tab);
  };

  // แสดงสถานะการโหลด
  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner-border text-primary" role="status">
          {/* <span className="visually-hidden">กำลังโหลด...</span> */}
        </div>
        {/* <p>กำลังโหลดข้อมูล...</p> */}
      </div>
    );
  }

  return (
    <div className="admin-page">
      <AdminSidebar user={user} />
      <div className="admin-header">
        <h1 className="admin-title">
          <i className="fas fa-book"></i> จัดการสื่อการสอน
        </h1>
        <div className="admin-breadcrumb">
          <Link to="/admin/dashboard">แดชบอร์ด</Link> /
          <span>จัดการสื่อการสอน</span>
        </div>
      </div>

      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === "approved" ? "active" : ""}`}
          onClick={() => handleTabChange("approved")}
        >
          <i className="fas fa-check-circle"></i> สื่อการสอนที่อนุมัติแล้ว
          <span className="badge">{resources.length}</span>
        </button>
        <button
          className={`admin-tab ${activeTab === "pending" ? "active" : ""}`}
          onClick={() => handleTabChange("pending")}
        >
          <i className="fas fa-clock"></i> สื่อการสอนรอการอนุมัติ
          <span className="badge">{pendingResources.length}</span>
        </button>
      </div>

      <div className="admin-card">
        {activeTab === "approved" ? (
          <>
            <div className="admin-card-header">
              <h2>สื่อการสอนที่อนุมัติแล้ว</h2>
            </div>
            <div className="admin-card-body">
              <div className="table-responsive">
                <table
                  className="table table-striped"
                  ref={approvedTableRef}
                  key="approved-table"
                >
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>รูปภาพ</th>
                      <th>ชื่อสื่อการสอน</th>
                      <th>ประเภท</th>
                      <th>ผู้อัปโหลด</th>
                      <th>หมวดหมู่</th>
                      <th>วิชา</th>
                      <th>ระดับชั้น</th>
                      <th>เข้าชม</th>
                      <th>ดาวน์โหลด</th>
                      <th>วันที่สร้าง</th>
                      <th>จัดการ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resources.map((resource) => (
                      <tr key={resource.id}>
                        <td>{resource.id}</td>
                        <td>
                          <img
                            src={
                              resource.thumbnail
                                ? `${process.env.REACT_APP_API_URL}/uploads/${resource.thumbnail}`
                                : "/assets/default-resource.jpg"
                            }
                            alt={resource.title}
                            className="resource-thumbnail-small"
                          />
                        </td>
                        <td>{resource.title}</td>
                        <td>
                          <span
                            className={`badge-file-type ${resource.file_type}`}
                          >
                            {resource.file_type}
                          </span>
                        </td>
                        <td>{resource.author_name}</td>
                        <td>{resource.category_name}</td>
                        <td>{resource.subject_name}</td>
                        <td>{resource.grade_level_name}</td>
                        <td>{resource.view_count}</td>
                        <td>{resource.download_count}</td>
                        <td>
                          {new Date(resource.created_at).toLocaleDateString(
                            "th-TH"
                          )}
                        </td>
                        <td>
                          <div className="action-buttons">
                            <Link
                              to={`/resources/${resource.id}`}
                              className="btn btn-sm btn-info"
                            >
                              <i className="fas fa-eye"></i>
                            </Link>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDelete(resource.id)}
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="admin-card-header">
              <h2>สื่อการสอนรอการอนุมัติ</h2>
            </div>
            <div className="admin-card-body">
              {pendingResources.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-check-circle"></i>
                  <p>ไม่มีสื่อการสอนที่รอการอนุมัติ</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table
                    className="table table-striped"
                    ref={pendingTableRef}
                    key="pending-table"
                  >
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>รูปภาพ</th>
                        <th>ชื่อสื่อการสอน</th>
                        <th>ประเภท</th>
                        <th>ผู้อัปโหลด</th>
                        <th>หมวดหมู่</th>
                        <th>วิชา</th>
                        <th>ระดับชั้น</th>
                        <th>วันที่สร้าง</th>
                        <th>จัดการ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingResources.map((resource) => (
                        <tr key={resource.id}>
                          <td>{resource.id}</td>
                          <td>
                            <img
                              src={
                                resource.thumbnail
                                  ? `${process.env.REACT_APP_API_URL}/uploads/${resource.thumbnail}`
                                  : "/assets/default-resource.jpg"
                              }
                              alt={resource.title}
                              className="resource-thumbnail-small"
                            />
                          </td>
                          <td>{resource.title}</td>
                          <td>
                            <span
                              className={`badge-file-type ${resource.file_type}`}
                            >
                              {resource.file_type}
                            </span>
                          </td>
                          <td>{resource.author_name}</td>
                          <td>{resource.category_name}</td>
                          <td>{resource.subject_name}</td>
                          <td>{resource.grade_level_name}</td>
                          <td>
                            {new Date(resource.created_at).toLocaleDateString(
                              "th-TH"
                            )}
                          </td>
                          <td>
                            <div className="action-buttons">
                              <Link
                                to={`/resources/${resource.id}`}
                                className="btn btn-sm btn-info"
                              >
                                <i className="fas fa-eye"></i>
                              </Link>
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => handleApprove(resource.id)}
                              >
                                <i className="fas fa-check"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleDelete(resource.id)}
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminResourcesPage;
